import type { ReportTableResponse } from '../../api/types';
import { formatCell, isNumeric } from './reportFormat';

/**
 * Any report, rendered.
 *
 * Written once against the generic shape rather than eight times against eight result types, which
 * is the whole reason {@code ReportTable} on the server is generic in the first place.
 *
 * Two details that are not decoration. The table sits inside its own horizontally scrolling
 * container, because REP-02 grouped by month is seven columns wide and a laptop in portrait is not
 * — and a page that scrolls sideways as a whole is the layout bug this product is most likely to
 * ship. And the totals row is a `tfoot`, so a screen reader announces it as the summary it is
 * rather than as one more branch (NFR-007).
 */
export function ReportTable({
  table,
  heading,
}: {
  readonly table: ReportTableResponse;
  readonly heading: (columnKey: string) => string;
}) {
  const columns = table.columns ?? [];
  const rows = table.rows ?? [];
  const totals = table.totals ?? {};
  const hasTotals = columns.some((column) => column.key && column.key in totals);

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={isNumeric(column.type) ? 'numeric' : undefined}
              >
                {heading(column.key ?? '')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // The label is unique per row by construction — it is what the report grouped by — but
            // a clinic with two branches of the same name would otherwise collide, so the index
            // joins it.
            <tr key={`${String(row[columns[0]?.key ?? 'label'] ?? '')}-${index}`}>
              {columns.map((column) => (
                <td key={column.key} className={isNumeric(column.type) ? 'numeric' : undefined}>
                  {formatCell(row[column.key ?? ''], column.type, table.currency)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {hasTotals ? (
          <tfoot>
            <tr>
              {columns.map((column, index) => (
                <td key={column.key} className={isNumeric(column.type) ? 'numeric' : undefined}>
                  {index === 0
                    ? heading('total')
                    : formatCell(totals[column.key ?? ''], column.type, table.currency)}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
